import { useState } from "react";
import { toast } from "sonner";
import { Sparkles, Image as ImageIcon, FileText, FileSignature, FileOutput, Loader2, Upload, X, LayoutTemplate, Megaphone, ShieldAlert, ClipboardCheck, UtensilsCrossed, CalendarClock, Settings2, Globe } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Textarea } from "../ui/textarea";
import { aiApi, absUrl } from "./aiApi";
import AIDocSkillDialog from "./AIDocSkillDialog";
import AIWordToPdfDialog from "./AIWordToPdfDialog";

const ICONS = { image_designer: ImageIcon, pdf_generator: FileText, document_generator: FileSignature, word_to_pdf: FileOutput, landing_page_builder: LayoutTemplate, marketing_designer: Megaphone, incident_manager: ShieldAlert, sop_manager: ClipboardCheck, menu_planner: UtensilsCrossed, staff_scheduling: CalendarClock, web_research: Globe };
const KINDS = [["poster", "Poster"], ["banner", "Banner"], ["kad", "Kad Ucapan"], ["ilustrasi", "Ilustrasi"]];
const SIZES = [["1:1", "1:1"], ["9:16", "9:16"], ["16:9", "16:9"], ["A4", "A4"]];

const Chip = ({ on, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${on ? "bg-pink-500 border-pink-500 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{children}</button>
);

export default function AISkillsMenu({ cid, canUpload, onCreated, onAsk, role }) {
  const [skills, setSkills] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState(null);
  const [docSkill, setDocSkill] = useState(null);
  const [wordPdf, setWordPdf] = useState(false);
  const navigate = useNavigate();

  const load = () => aiApi.skills().then(setSkills).catch(() => toast.error("Gagal memuat skills"));
  const pick = (s) => {
    if (!s.ready) return toast.info(`${s.label} akan datang tidak lama lagi`);
    if (!s.allowed) return toast.error(`${s.label} tidak dibenarkan untuk peranan anda`);
    setOpen(false);
    if (s.custom) return onAsk?.(`Jalankan skill "${s.label}" (skill_id=${s.id}).`);
    if (s.id === "word_to_pdf") return setWordPdf(true);
    if (s.id === "web_research") return onAsk?.(`Guna skill Carian Internet (skill_id=web_research). Terangkan ringkas apa yang boleh dibuat dan tanya apa yang saya mahu cari.`);
    if (s.id === "landing_page_builder") return navigate("/mimi/landing-builder");
    if (s.managed) return navigate(`/mimi/skills/${s.id}`);
    if (s.id !== "image_designer") return setDocSkill(s.id);
    setForm({ prompt: "", kind: "poster", size: "1:1", format: "png" });
  };
  const onRef = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try { setRef(await aiApi.upload(f)); } catch (err) { toast.error(err.response?.data?.detail || "Gagal muat naik"); }
  };
  const submit = async () => {
    setBusy(true);
    try {
      const d = await aiApi.createImageJob({ ...form, conversation_id: cid, reference_file_id: ref?.id || null });
      onCreated(d);
      setForm(null); setRef(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal memulakan Image Designer"); } finally { setBusy(false); }
  };
  const q = skills?.quota;

  return (
    <>
      <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
        <PopoverTrigger asChild>
          <button className="h-8 px-3 rounded-full bg-gradient-to-r from-pink-500 to-amber-400 text-white text-xs font-bold flex items-center gap-1.5 shadow-sm hover:brightness-105 transition-[filter]" data-testid="ai-skills-btn"><Sparkles className="h-4 w-4" /> Skills</button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-72 p-2 z-[10001]" data-testid="ai-skills-menu">
          {!skills ? <div className="grid place-items-center h-20"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
            <div className="space-y-1.5">
              {skills.skills.map((s) => {
                const Icon = ICONS[s.id] || Sparkles;
                return (
                  <button key={s.id} onClick={() => pick(s)} data-testid={`ai-skill-${s.id}`}
                    className={`w-full flex items-start gap-2.5 rounded-xl p-2.5 text-left transition-colors ${s.ready && s.allowed ? "hover:bg-pink-50" : "opacity-60"}`}>
                    <span className="h-9 w-9 shrink-0 rounded-xl bg-pink-100 text-pink-600 grid place-items-center"><Icon className="h-4.5 w-4.5" /></span>
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5 text-[13px] font-bold text-slate-800">{s.label}{!s.ready && <span className="text-[9.5px] font-bold uppercase bg-slate-100 text-slate-500 rounded-full px-1.5 py-0.5">Akan datang</span>}</span>
                      <span className="block text-[11px] text-slate-500">{s.desc}</span>
                    </span>
                  </button>
                );
              })}
              {role !== "ibu_bapa" && <button onClick={() => { setOpen(false); navigate("/mimi/skills"); }} data-testid="ai-skills-manage" className="w-full flex items-center gap-2 rounded-xl p-2 text-xs font-bold text-pink-600 hover:bg-pink-50"><Settings2 className="h-4 w-4" />Pengurusan Skill</button>}
              {q && <div className="px-2 pt-1 text-[10.5px] text-slate-400" data-testid="ai-skills-quota">Kuota gambar hari ini: {q.unlimited ? "tanpa had" : `${q.remaining}/${q.limit} berbaki`}</div>}
            </div>
          )}
        </PopoverContent>
      </Popover>
      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="z-[10002] max-w-md rounded-3xl" data-testid="ai-image-designer-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ImageIcon className="h-5 w-5 text-pink-500" /> Image Designer</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-3">
              <div><div className="text-xs font-bold text-slate-600 mb-1.5">Jenis</div><div className="flex flex-wrap gap-1.5">{KINDS.map(([k, l]) => <Chip key={k} on={form.kind === k} onClick={() => setForm({ ...form, kind: k })} testid={`ai-image-kind-${k}`}>{l}</Chip>)}</div></div>
              <div><div className="text-xs font-bold text-slate-600 mb-1.5">Saiz</div><div className="flex flex-wrap gap-1.5">{SIZES.map(([k, l]) => <Chip key={k} on={form.size === k} onClick={() => setForm({ ...form, size: k })} testid={`ai-image-size-${k.replace(":", "x")}`}>{l}</Chip>)}</div></div>
              <div><div className="text-xs font-bold text-slate-600 mb-1.5">Format</div><div className="flex flex-wrap gap-1.5">{[["png", "PNG"], ["jpeg", "JPG"]].map(([k, l]) => <Chip key={k} on={(form.format || "png") === k} onClick={() => setForm({ ...form, format: k })} testid={`ai-image-format-${k}`}>{l}</Chip>)}</div></div>
              <div><div className="text-xs font-bold text-slate-600 mb-1.5">Arahan</div>
                <Textarea rows={4} value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} placeholder="Cth: Poster Hari Sukan 2026, kanak-kanak berlari ceria, teks 'HARI SUKAN DESTINY JUNIOR'" className="rounded-xl text-[13px]" data-testid="ai-image-prompt" />
              </div>
              {canUpload && (
                <div className="flex items-center gap-2">
                  <label className="h-8 px-3 rounded-full border border-dashed border-slate-300 text-xs font-semibold text-slate-600 inline-flex items-center gap-1 cursor-pointer hover:bg-slate-50" data-testid="ai-image-ref-btn">
                    <Upload className="h-3.5 w-3.5" /> Gambar rujukan (pilihan)<input type="file" accept="image/*" hidden onChange={onRef} data-testid="ai-image-ref-input" />
                  </label>
                  {ref && <span className="flex items-center gap-1 text-[11px] text-slate-600"><img src={absUrl(ref.url)} alt="" className="h-7 w-7 rounded object-cover" /><button onClick={() => setRef(null)} aria-label="Buang"><X className="h-3.5 w-3.5" /></button></span>}
                </div>
              )}
              <button disabled={busy || form.prompt.trim().length < 3} onClick={submit} className="w-full h-11 rounded-2xl bg-gradient-to-r from-pink-500 to-amber-400 text-white font-bold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="ai-image-generate-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Jana Gambar
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <AIWordToPdfDialog open={wordPdf} cid={cid} canUpload={canUpload} onClose={() => setWordPdf(false)} onCreated={onCreated} onAsk={onAsk} />
      <AIDocSkillDialog skill={docSkill} role={role} cid={cid} onClose={() => setDocSkill(null)} onCreated={onCreated} />
    </>
  );
}
